import React, { useState, useRef, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://cloudunify.run.place/api';

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm your CloudUnify AI assistant. Ask me about your servers, costs, or deployments." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeptPicker, setShowDeptPicker] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const token = localStorage.getItem('mcp_token');
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text }),
      });
      const data = await res.json();
      setMessages(m => [...m, { role: 'assistant', text: data.reply || data.error || 'Something went wrong.' }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: 'Connection error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 90, width: 360, maxWidth: 'calc(100vw - 48px)',
          height: 480, maxHeight: 'calc(100vh - 140px)',
          background: '#131a2b', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16, boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column', zIndex: 9999, overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(99,102,241,0.02))',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9, background: '#6366f1',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
              }}>🤖</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>CloudUnify Assistant</div>
                <div style={{ fontSize: 11, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                  Online
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{
              background: 'none', border: 'none', color: '#8899b4', fontSize: 18, cursor: 'pointer', padding: 4,
            }}>✕</button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                background: m.role === 'user' ? '#6366f1' : 'rgba(255,255,255,0.06)',
                color: m.role === 'user' ? '#fff' : '#e2e8f0',
                padding: '9px 13px', borderRadius: 12,
                borderBottomRightRadius: m.role === 'user' ? 4 : 12,
                borderBottomLeftRadius: m.role === 'assistant' ? 4 : 12,
                fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
              }}>
                {m.text}
              </div>
            ))}
            {loading && (
              <div style={{
                alignSelf: 'flex-start', background: 'rgba(255,255,255,0.06)', color: '#8899b4',
                padding: '9px 13px', borderRadius: 12, borderBottomLeftRadius: 4, fontSize: 13,
              }}>
                Thinking...
              </div>
            )}
          </div>

          <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {!showDeptPicker ? (
              <button
                onClick={() => setShowDeptPicker(true)}
                style={{
                  width: '100%', padding: '7px', borderRadius: 8, background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                👤 Talk to a human
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: '#8899b4', marginBottom: 2 }}>What do you need help with?</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['Support', 'Sales', 'Billing'].map(dept => (
                    <button
                      key={dept}
                      onClick={() => {
                        let userObj = {};
                        try { userObj = JSON.parse(localStorage.getItem('mcp_user') || '{}'); } catch (e) {}
                        const name = userObj.name || 'Guest';
                        const email = userObj.email || '';
                        if (window.Tawk_API) {
                          window.Tawk_API.setAttributes({ name, email, department: dept }, () => {});
                          if (window.Tawk_API.addTags) window.Tawk_API.addTags([dept], () => {});
                          window.Tawk_API.showWidget && window.Tawk_API.showWidget();
                          window.Tawk_API.maximize();
                          setOpen(false);
                        } else {
                          window.open(`mailto:${dept === 'Billing' ? 'billing' : dept === 'Sales' ? 'info' : 'support'}@cloudunify.run.place`, '_blank');
                        }
                        setShowDeptPicker(false);
                      }}
                      style={{
                        flex: 1, padding: '6px', borderRadius: 7, background: 'rgba(99,102,241,0.1)',
                        border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your servers, costs..."
              rows={1}
              style={{
                flex: 1, resize: 'none', background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
                padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit',
              }}
            />
            <button onClick={send} disabled={loading || !input.trim()} style={{
              background: '#6366f1', border: 'none', borderRadius: 10, width: 38, height: 38,
              color: '#fff', fontSize: 16, cursor: loading ? 'default' : 'pointer',
              opacity: loading || !input.trim() ? 0.5 : 1, flexShrink: 0,
            }}>→</button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'fixed', bottom: 24, right: 90, width: 56, height: 56, borderRadius: '50%',
          background: '#6366f1', border: 'none', boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
          color: '#fff', fontSize: 24, cursor: 'pointer', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
      >
        {open ? '✕' : '💬'}
      </button>
    </>
  );
}
